import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Extension } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import tippy from 'tippy.js';
import { resourceReferenceContent, ResourceChatChoice } from '../../../../services/resources/ResourceChatService';
import './ResourceSuggestion.css';

// Only a slash at the start of a text block or after whitespace starts a search.
// URL paths, dates, fractions, file paths and ordinary inline slashes stay text.
export function isResourceShortcutBoundary(before: string) {
  return before.length === 0 || /\s$/.test(before);
}

export const ResourceList = forwardRef((props: any, ref) => {
  const [selected, setSelected] = useState(0);
  useEffect(() => setSelected(0), [props.items]);
  const choose = (index: number) => { if (props.items[index]) { setSelected(index); props.command(props.items[index]); } };
  useImperativeHandle(ref, () => ({ onKeyDown: ({ event }: { event: KeyboardEvent }) => {
    if ((event.key === 'ArrowUp' || event.key === 'ArrowDown') && props.items.length) {
      setSelected(value => (value + (event.key === 'ArrowUp' ? -1 : 1) + props.items.length) % props.items.length);
      return true;
    }
    if ((event.key === 'Enter' || event.key === 'Tab') && props.items.length) { choose(selected); return true; }
    return false;
  } }));
  return <><div className="chat-resource-suggestions" role="listbox" aria-label="Resource suggestions">
    {props.items.length ? props.items.map((item: ResourceChatChoice, index: number) => <button type="button" key={item.id}
      role="option" aria-selected={index === selected} tabIndex={-1} className={index === selected ? 'is-selected' : ''} onMouseDown={event => event.preventDefault()} onClick={() => choose(index)}>
      <span className="chat-resource-suggestions__symbol" aria-hidden="true">/</span><span><strong>{item.title}</strong><small>{item.providerName}</small></span>
    </button>) : <div role="option" aria-selected={false} aria-disabled="true">No available resources match.</div>}
  </div><span className="sr-only" role="status" aria-live="polite" aria-atomic="true">{props.items.length && props.items[selected]
    ? `${selected + 1} of ${props.items.length}: ${props.items[selected].title}, ${props.items[selected].providerName}. Press Enter to insert resource.`
    : 'No available resources match.'}</span></>;
});
ResourceList.displayName = 'ResourceList';

export function createResourceSuggestionOptions(search: (query: string) => Promise<ResourceChatChoice[]>, preview: (id: string) => Promise<void>, onError: () => void) {
  return {
    char: '/', pluginKey: new PluginKey('resourceReferenceSuggestion'), allowedPrefixes: [' '],
    allow: ({ state, range }: any) => {
      const resolved = state.doc.resolve(range.from);
      return !resolved.parent.type.spec.code && !state.doc.rangeHasMark(range.from, range.to, state.schema.marks.link)
        && isResourceShortcutBoundary(resolved.parent.textBetween(0, resolved.parentOffset));
    },
    items: ({ query }: { query: string }) => search(query),
    command: ({ editor, range, props }: any) => {
      const originalDoc = editor.state.doc;
      const original = editor.state.doc.textBetween(range.from, range.to);
      // Selection can change while permission is being rechecked. Never replace a stale range.
      void preview(props.id).then(() => {
        if (editor.isDestroyed || editor.state.doc !== originalDoc || range.to > editor.state.doc.content.size || editor.state.doc.textBetween(range.from, range.to) !== original) return;
        editor.chain().focus().insertContentAt(range, [resourceReferenceContent(props.id), { type: 'text', text: ' ' }]).run();
      }).catch(onError);
    },
    render: () => {
      let component: ReactRenderer<any> | undefined; let popup: any[] | undefined;
      return {
        onStart: (props: any) => {
          component = new ReactRenderer(ResourceList, { props, editor: props.editor });
          if (!props.clientRect) return;
          popup = tippy('body', { getReferenceClientRect: props.clientRect, appendTo: () => document.body, content: component.element,
            showOnCreate: true, interactive: true, trigger: 'manual', placement: 'top-start', maxWidth: 'min(360px, calc(100vw - 24px))' });
        },
        onUpdate: (props: any) => { component?.updateProps(props); if (props.clientRect) popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect }); },
        onKeyDown: (props: any) => { if (props.event.key === 'Escape') { popup?.[0]?.hide(); return true; } return component?.ref?.onKeyDown(props) ?? false; },
        onExit: () => { popup?.[0]?.destroy(); component?.destroy(); },
      };
    },
  };
}

export const ResourceSuggestion = Extension.create<{ search: (query: string) => Promise<ResourceChatChoice[]>; preview: (id: string) => Promise<void>; onError: () => void }>({
  name: 'resourceReferenceSuggestion',
  addOptions() { return { search: async () => [], preview: async () => {}, onError: () => {} }; },
  addProseMirrorPlugins() { return [Suggestion({ editor: this.editor, ...createResourceSuggestionOptions(this.options.search, this.options.preview, this.options.onError) })]; },
});
